import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { inject } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { set, action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { guidFor } from '@ember/object/internals';
import { task, timeout, didCancel } from 'ember-concurrency';
import { beebeeAjaxGet, normalizeProvinceCode, hereToAddress } from 'beebee-base/lib/utility';
import { paesiMondo, paesiMondoAlfa3 } from 'beebee-base/lib/list_taxes';
import getConfig from 'beebee-base/lib/get-config';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{this._autoFillCountyTrigger}}\n{{! ── Via + Numero ───────────────────────────────────────────────────── }}\n<div class=\'grid-x grid-padding-x address-sub-row\' ...attributes>\n  <div class=\'cell medium-8\'>\n    <span class=\'beebeeLabel\'>\n      {{t \'street\'}}\n      {{#if this.required}}\n        <span class=\'required\'>{{t \'required\'}}</span>\n      {{/if}}\n    </span>\n    <div class=\'here-autocomplete-wrapper\'>\n      <Input\n        @value={{this.item.location_address_street}}\n        placeholder={{t \'address.street.placeholder\'}}\n        autocomplete={{if @allowAutofill \'on\' \'off\'}}\n        disabled={{this.disabled}}\n        {{! one control, three callbacks: each carries its own hook so a test\n            that drives one is never credited for the others }}\n        data-test-on-street-input\n        data-test-recreate-text-address-task\n        data-test-close-street-dropdown\n        {{on \'input\' this.onStreetInput}}\n        {{on \'change\' (perform this.recreateTextAddressTask)}}\n        {{on \'blur\' this.closeStreetDropdown}}\n      />\n      <BbDropdown @id=\'zf-street-here-{{this.elementId}}\' class=\'no-padding here-zf-dropdown\'>\n        <ul class=\'action-sheet-results vertical menu\'>\n          {{#each this.streetSuggestions as |suggestion|}}\n            <li>\n              <a\n                title=\'{{suggestion.address.label}}\'\n                data-test-on-street-change\n                {{on \'mousedown\' (fn (perform this.onStreetChange) suggestion)}}\n              >\n                <div class=\'addressResultTitle\'>\n                  {{suggestion.address.label}}\n                </div>\n              </a>\n            </li>\n          {{/each}}\n        </ul>\n      </BbDropdown>\n    </div>\n  </div>\n  <div class=\'cell medium-4\'>\n    <span class=\'beebeeLabel\'>\n      {{t \'street number\'}}\n      {{#if this.required}}\n        <span class=\'required\'>{{t \'required\'}}</span>\n      {{/if}}\n    </span>\n    <Input\n      @value={{this.item.location_address_house}}\n      placeholder={{t \'address.house.placeholder\'}}\n      autocomplete={{if @allowAutofill \'on\' \'off\'}}\n      disabled={{this.disabled}} data-test-recreate-text-address {{on \'change\' (perform this.recreateTextAddressTask)}}\n    />\n  </div>\n</div>\n\n{{! ── Comune/Città + Provincia + CAP ────────────────────────────────── }}\n<div class=\'grid-x grid-padding-x address-sub-row\'>\n  {{#if this._isItaly}}\n    <div class=\'cell medium-4\'>\n      <span class=\'beebeeLabel\'>\n        {{t \'city\'}}\n        {{#if this.required}}\n          <span class=\'required\'>{{t \'required\'}}</span>\n        {{/if}}\n      </span>\n      <PowerSelect\n        @search={{perform this.searchComune}}\n        @selected={{this.selectedComune}}\n        @onChange={{this.onComuneChange}}\n        @renderInPlace={{true}}\n        @searchEnabled={{true}}\n        @disabled={{this.disabled}}\n        @placeholder={{t \'address.city.placeholder\'}}\n        @searchPlaceholder={{t \'address.comune.search.placeholder\'}}\n        @noMatchesMessage={{t \'address.comune.no.match\'}} data-test-on-comune-change\n        as |comune|\n      >\n        <div class=\'comune-item\'>\n          {{comune.comune}} {{comune.cap}}\n          {{#if comune.code_prov}}\n            <span class=\'prov-badge\'>{{comune.code_prov}}</span>\n          {{/if}}\n        </div>\n      </PowerSelect>\n    </div>\n    <div class=\'cell medium-2\'>\n      <span class=\'beebeeLabel\'>\n        {{t \'address.province\'}}\n      </span>\n      <Input @value={{this.item.location_address_county}} disabled={{true}} placeholder=\'––\' />\n    </div>\n  {{else}}\n    <div class=\'cell medium-4\'>\n      <span class=\'beebeeLabel\'>\n        {{t \'city\'}}\n      </span>\n      <Input\n        @value={{this.item.location_address_city}}\n        placeholder={{t \'address.city.placeholder\'}}\n        autocomplete={{if @allowAutofill \'on\' \'off\'}}\n        disabled={{this.disabled}} data-test-recreate-text-address-2 {{on \'change\' (perform this.recreateTextAddressTask)}}\n      />\n    </div>\n    <div class=\'cell medium-2\'>\n      <span class=\'beebeeLabel\'>\n        {{t \'address.state.region\'}}\n      </span>\n      <Input\n        @value={{this.item.location_address_county}}\n        placeholder={{t \'address.county.placeholder\'}}\n        autocomplete={{if @allowAutofill \'on\' \'off\'}}\n        disabled={{this.disabled}} data-test-recreate-text-address-3 {{on \'change\' (perform this.recreateTextAddressTask)}}\n      />\n    </div>\n  {{/if}}\n  <div class=\'cell medium-3\'>\n    <span class=\'beebeeLabel\'>\n      {{t \'cap\'}}\n    </span>\n    <Input\n      @value={{this.item.location_address_postal_code}}\n      placeholder={{t \'address.postal.placeholder\'}}\n      inputmode=\'numeric\'\n      autocomplete={{if @allowAutofill \'on\' \'off\'}}\n      disabled={{this.isCapDisabled}} data-test-recreate-text-address-4 {{on \'change\' (perform this.recreateTextAddressTask)}}\n    />\n  </div>\n  <div class=\'cell medium-3\'>\n    <span class=\'beebeeLabel\'>\n      {{t \'nation\'}}\n    </span>\n    <PowerSelect\n      @options={{this.paesiMondoWithCountry}}\n      @selected={{this.item.location_address_country}}\n      @onChange={{this.changeCountry}}\n      @searchEnabled={{true}}\n      @renderInPlace={{true}}\n      @disabled={{this.disabled}}\n      @placeholder={{t \'nation\'}} data-test-change-country\n      as |paese|\n    >\n      {{paese}}\n    </PowerSelect>\n  </div>\n</div>\n\n{{! ── MORE INFO (sempre visibile) ──────────────────────────────────── }}\n{{#if this.moreInfo}}\n  <div class=\'grid-x grid-padding-x address-edit-check\'>\n    <div class=\'cell medium-auto\'>\n      <BbCheckbox\n        @content={{this.item.is_invoice}}\n        @setContent={{this.setInvoiceAddress}}\n        @disabled={{this.disabled}}\n        @label={{t \'billing address\'}}\n      />\n    </div>\n    <div class=\'cell medium-auto\'>\n      <BbCheckbox\n        @content={{this.item.is_consegna}}\n        @disabled={{this.disabled}}\n        @setContent={{this.setDeliveryAddress}}\n        @label={{t \'delivery address\'}}\n      />\n    </div>\n  </div>\n\n  {{#if this.item.is_consegna}}\n    <div class=\'grid-x grid-padding-x input-compact\'>\n      <div class=\'cell medium-4\'>\n        <span class=\'beebeeLabel\'>\n          {{t \'floor\'}}\n        </span>\n        <Input @type=\'text\' @value={{this.item.piano}} placeholder=\'e.g. 2\' />\n      </div>\n      <div class=\'cell medium-8\'>\n        <span class=\'beebeeLabel\'>\n          {{t \'note\' count=2}}\n        </span>\n        <Textarea\n          @value={{this.item.notes}}\n          class=\'{{if this.item.errors.notes \"error\"}}\'\n          placeholder=\'{{t \"note\" count=2}}\'\n        />\n      </div>\n    </div>\n  {{/if}}\n\n  {{#if this.canDelete}}\n    <div class=\'text-right\'>\n      <span\n        role=\'button\'\n        data-toggle=\'zf-remove-address-{{this.elementId}}\'\n        class=\'button alert flat\'\n      >\n        <FaIcon @icon=\'trash-alt\' />\n        {{t \'delete address\'}}\n      </span>\n    </div>\n  {{/if}}\n\n  <BbDropdown @id=\'zf-remove-address-{{this.elementId}}\'>\n    <h4 class=\'label hollow\'>\n      {{t \'do you want delete\'}}?\n    </h4>\n\n    <button\n      type=\'button\'\n      class=\'button alert expanded {{if this.removeAddressTask.isRunning \"loading\"}}\'\n      disabled={{this.removeAddressTask.isRunning}}\n      data-test-remove-address\n      {{on \'click\' (fn (perform this.removeAddressTask) this.item)}}\n    >\n      {{t \'delete\'}}\n    </button>\n  </BbDropdown>\n{{/if}}");

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0;
const DEBOUNCE_MS = 500;

// Shared across all EditSingleAddress instances (one per address on the page):
// counts open street-autocomplete dropdowns so the body class is only removed
// once the last one closes.
let openDropdownCount = 0;

// HERE Maps API key: prefer per-app config (ENV.APP.here.apiKey, same shape
// already used by eventicerimoniale). The fallback keeps apps without that
// config entry working; rotating the key only requires updating app configs.
const HERE_API_KEY_FALLBACK = 'xclBSDj4_2_MroIvNErLrbdRGh9kMT2bu4BQ-TDPsKk';
let EditSingleAddressComponent = (_dec = task(function* (city) {
  yield timeout(300);
  const county = this.item?.location_address_county;
  if (!this.item || !city || county && county.length === 2) return;
  let results;
  try {
    results = yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/comuni_search`, this.session.token, {
      q: city
    });
  } catch {
    return;
  }
  if (this.item?.location_address_city !== city) return;
  if (!results?.length) return;

  // A city query can legitimately return several comuni (prefix matches, or
  // homonyms in different provinces). Only the exact-name hit — or a set that
  // agrees on one province — identifies the province; the old
  // `length === 1` test left every ambiguous city holding whatever wrong
  // value the fill path had written.
  const target = String(city).trim().toLowerCase();
  const exact = results.find(r => r.comune?.trim().toLowerCase() === target);
  const codes = new Set(results.map(r => r.code_prov).filter(Boolean));
  let rawCode = null;
  if (exact) {
    rawCode = exact.code_prov;
  } else if (codes.size === 1) {
    rawCode = [...codes][0];
  }
  const code = normalizeProvinceCode(rawCode);
  if (code) {
    set(this.item, 'location_address_county', code);
  }

  // CAP is per-comune, so it can only be filled from an unambiguous match.
  const capSource = exact ?? (results.length === 1 ? results[0] : null);
  if (capSource) {
    set(this.item, 'location_address_postal_code', capSource.cap);
  }
}).drop(), _dec2 = task(function* (q) {
  yield timeout(DEBOUNCE_MS);
  if (!q || q.length < 2) {
    this.closeStreetDropdown();
    return;
  }
  const params = new URLSearchParams({
    q,
    language: 'it',
    apiKey: this._hereApiKey
  });
  let data;
  try {
    const response = yield fetch(`https://autocomplete.search.hereapi.com/v1/autocomplete?${params.toString()}`, {
      method: 'GET',
      credentials: 'omit'
    });
    if (!response.ok) {
      this.closeStreetDropdown();
      return;
    }
    data = yield response.json();
  } catch {
    this.closeStreetDropdown();
    return;
  }
  this.streetSuggestions = data?.items ?? [];
  if (this.streetSuggestions.length > 0) {
    this._openStreetDropdown();
  } else {
    this.closeStreetDropdown();
  }
}).restartable(), _dec3 = task(function* (item) {
  // `item` is the selected HERE street SUGGESTION (its id drives the lookup),
  // NOT the address record — that is `this.item`. Capture the record up front:
  // this .drop() task is fired by a click, so an @item swap during the HERE
  // lookup below is NOT auto-cancelled by ember-concurrency (the host stays
  // alive, only the arg changes). Writing the geocoded payload onto the LIVE
  // `this.item` would land THIS address's street/city/county/CAP/coordinates
  // onto whichever address the parent list swapped in (e.g. after a
  // removeAddressTask splice or a contact reload) — fiscal invoice-address and
  // shipping corruption on the wrong record.
  const addressRecord = this.item;
  if (!item || !addressRecord) {
    return;
  }
  const params = new URLSearchParams({
    id: item.id,
    apiKey: this._hereApiKey
  });
  let data;
  try {
    const response = yield fetch(`https://lookup.search.hereapi.com/v1/lookup?${params.toString()}`, {
      method: 'GET',
      credentials: 'omit'
    });
    if (!response.ok) return;
    data = yield response.json();
  } catch {
    return;
  }

  // The address arg can swap or unload during the two awaits above; bail
  // before writing rather than corrupting a different record. (POJO hosts —
  // heliguides sign-request form — have no isDestroyed; the strict identity
  // check covers them.)
  if (this.item !== addressRecord || addressRecord.isDestroyed || addressRecord.isDestroying) {
    return;
  }
  if (data) {
    const addressItem = hereToAddress(data);

    // HERE returns the EXTENDED province name in `county` (e.g. "Oristano",
    // "Città Metropolitana di Milano") and only sometimes a `countyCode`.
    // For Italy the field must end up as the 2-letter code, so take the code
    // when present, otherwise map the name offline; when neither resolves,
    // write null and let autoFillCountyTask settle it from comuni_search —
    // storing the long name was the bug (province shown as "Oristano" in a
    // disabled input the user cannot correct).
    const countyCode = normalizeProvinceCode(data.address?.countyCode);
    if (countyCode) {
      addressItem.location_address_county = countyCode;
    } else if (this._isItalyPayload(data.address, addressItem)) {
      // No usable countyCode on an Italian address: map the extended name, and
      // when even that fails leave the field empty for autoFillCountyTask
      // rather than persisting a long name into a disabled input.
      addressItem.location_address_county = normalizeProvinceCode(addressItem.location_address_county);
    }

    // POP-251: the autocomplete menu offers `street` / `streetSection` hits
    // alongside full `houseNumber` ones, and those carry no houseNumber (nor,
    // often, a postalCode) by definition. Writing the mapped payload as a
    // whole object meant `location_address_house: undefined` overwrote the
    // civico the operator had just typed into its own input — the record was
    // then saved, and recreateTextAddressTask rebuilt `name` /
    // `location_address_text` from the surviving fields, so the loss became
    // permanent. Write only the keys HERE actually resolved. `null` is NOT
    // filtered: the county block above sets it deliberately to hand the field
    // over to autoFillCountyTask.
    const patch = Object.fromEntries(Object.entries(addressItem).filter(([, value]) => value !== undefined));

    // Some payloads return the missing civico as an explicit null/'' instead
    // of omitting it, which the filter above lets through. A HERE result
    // without a house number must never clear the one the user typed.
    if (!patch.location_address_house) {
      delete patch.location_address_house;
    }
    if (addressRecord.setProperties) {
      addressRecord.setProperties(patch);
    } else {
      for (const key in patch) {
        set(addressRecord, key, patch[key]);
      }
    }
    if (addressRecord.my_dirty) {
      set(addressRecord, 'my_dirty', !addressRecord.my_dirty);
    }
    this.closeStreetDropdown();
    // A street-level hit leaves the CAP untouched above, so the field must
    // stay editable for the operator to fill it in.
    this._capFromHere = !!patch.location_address_postal_code;
    try {
      yield this.recreateTextAddressTask.perform();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }

    // recreateTextAddressTask above awaited a debounce; re-verify the arg has
    // not swapped before persisting, so the save targets the record we geocoded.
    if (this.item !== addressRecord) return;

    // Salva l'address solo se ha già un ID (record già persistito).
    // I nuovi address (isNew, id: null) verranno committati quando
    // il contact viene salvato via saveRecordRelated, evitando race
    // condition che creano indirizzi duplicati nel backend.
    //
    // `save` must be checked as well as `id`. This editor is deliberately
    // bound to PLAIN OBJECTS in some hosts — beebeeboard-booking's
    // heliguides sign-request form passes an entry of
    // `PHCustomData.principale`, a POJO of `location_*` fields that carries
    // an id but travels with the form payload rather than being persisted on
    // its own. Those have no `save()`, so the standalone save crashed the
    // page with "e.save is not a function" as soon as the customer picked a
    // street (Sentry EMBER-BOOKING-V2). A POJO belongs to the same case as a
    // brand-new record above: fill the fields in, let the host persist them.
    if (this.session.token && addressRecord.id && typeof addressRecord.save === 'function') {
      yield this.applicationController.saveRecord(addressRecord, false);
    }
  }
}).drop(), _dec4 = task(function* (q) {
  yield timeout(DEBOUNCE_MS);
  if (!q || q.length < 2) return [];
  try {
    return yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/comuni_search`, this.session.token, {
      q
    });
  } catch {
    return [];
  }
}).restartable(), _dec5 = task(function* () {
  const item = this.item;
  if (!item) {
    return;
  }
  const raw = item.getProperties ? item.getProperties('location_address_street', 'location_address_house', 'location_address_postal_code', 'location_address_city', 'location_address_country') : item;

  // Normalize with ?? (not destructuring defaults): backend attributes can be
  // null, which would otherwise render the literal string "null" in the text.
  const location_address_street = raw.location_address_street ?? '';
  const location_address_house = raw.location_address_house ?? '';
  const location_address_postal_code = raw.location_address_postal_code ?? '';
  const location_address_city = raw.location_address_city ?? '';
  const location_address_country = raw.location_address_country ?? '';
  const newAddressText = `${location_address_street} ${location_address_house}<br/>${location_address_postal_code} ${location_address_city}<br/>${location_address_country}`;
  set(item, 'location_address_text', newAddressText);
  const addressName = `${location_address_street} ${location_address_house} ${location_address_postal_code} ${location_address_city} ${location_address_country}`;
  set(item, 'name', addressName);
  if (this.changeAction) {
    this.changeAction.perform();
  }
  yield timeout(500);
}).restartable(), _dec6 = task(function* (item) {
  const removeIsInvoice = !!item.is_invoice;

  // Delete on the server first: removing the item from the local list before
  // a failed DELETE would leave the UI and the backend out of sync.
  // destroyRecord NEVER rejects — on a refusal it notifies the user, rolls the
  // record back and resolves `false`. Ignoring that answer took the row off
  // screen anyway and, when the deleted address was the billing one, promoted
  // AND SAVED another address as the billing one for a delete that never
  // happened.
  const destroyed = yield this.applicationController.destroyRecord(item);
  if (this.isDestroying || this.isDestroyed) return;
  if (destroyed === false) return false;
  const _i = this.addresses.indexOf(item);
  if (_i !== -1) {
    this.addresses.splice(_i, 1);
  }
  if (removeIsInvoice && this.addresses.length) {
    const hasInvoice = this.addresses.find(a => a.is_invoice === true);
    if (!hasInvoice) {
      set(this.addresses[0], 'is_invoice', true);
      yield this.applicationController.saveRecord(this.addresses[0]);
    }
  }
  if (this.changeAction) {
    this.changeAction.perform();
  }
}).drop(), _class = class EditSingleAddressComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "session", _descriptor2, this);
    _initializerDefineProperty(this, "_capFromHere", _descriptor3, this);
    _initializerDefineProperty(this, "streetSuggestions", _descriptor4, this);
    // Tracks whether THIS instance's dropdown is currently counted in
    // openDropdownCount, so destroy/close only decrements once per instance.
    this._dropdownOpen = false;
    _initializerDefineProperty(this, "autoFillCountyTask", _descriptor5, this);
    _initializerDefineProperty(this, "searchStreet", _descriptor6, this);
    _initializerDefineProperty(this, "onStreetChange", _descriptor7, this);
    // ── Comuni ISTAT ─────────────────────────────────────────────────────────
    _initializerDefineProperty(this, "searchComune", _descriptor8, this);
    // ── Utilità ──────────────────────────────────────────────────────────────
    _initializerDefineProperty(this, "recreateTextAddressTask", _descriptor9, this);
    _initializerDefineProperty(this, "removeAddressTask", _descriptor0, this);
  }
  willDestroy() {
    super.willDestroy(...arguments);
    if (this._dropdownOpen) {
      this._dropdownOpen = false;
      openDropdownCount = Math.max(0, openDropdownCount - 1);
      if (openDropdownCount === 0) {
        document.body?.classList.remove('dropdown-open');
      }
    }
  }
  get selectedComune() {
    const city = this.item?.location_address_city;
    return city ? {
      comune: city
    } : null;
  }

  // Reactive trigger: read on every render, schedules the repair when the
  // province is missing or is not a 2-character code. An Italian address MUST
  // store the 2-letter code (`OR`), never the extended name (`Oristano`) — the
  // province input is disabled for Italy, so a wrong value here is unreachable
  // for the user and flows straight into invoices, SDI XML and FSE payloads.
  get _autoFillCountyTrigger() {
    const city = this.item?.location_address_city;
    const county = this.item?.location_address_county;
    if (this._isItaly && (!county || county.length !== 2)) {
      scheduleOnce('afterRender', this, this._triggerAutoFillCounty, city);
    }
    return null;
  }
  _triggerAutoFillCounty(city) {
    if (this.isDestroying || this.isDestroyed) return;

    // Extended province names resolve offline: no reason to spend a request (or
    // to leave the bad value on screen for a debounce) when `Oristano` and
    // `Città Metropolitana di Milano` map to `OR`/`MI` from the local table.
    const mapped = normalizeProvinceCode(this.item?.location_address_county);
    if (mapped) {
      set(this.item, 'location_address_county', mapped);
      return;
    }
    if (!city) return;
    if (!this.autoFillCountyTask.isRunning) {
      this.autoFillCountyTask.perform(city);
    }
  }
  get elementId() {
    return guidFor(this);
  }
  get moreInfo() {
    return this.args.moreInfo ?? true;
  }
  get canDelete() {
    return this.args.canDelete ?? true;
  }
  get required() {
    return this.args.required ?? false;
  }
  get disabled() {
    return this.args.disabled;
  }
  get _isItaly() {
    const code = this.item?.location_address_country_code?.toLowerCase();
    const country = this.item?.location_address_country?.toLowerCase();
    return code === 'ita' || code === 'it' || code === 'italia' || country === 'italia' || country === 'italy';
  }

  // `_isItaly` reads the record, which during a HERE fill still holds the OLD
  // country: the incoming payload decides whether the 2-char province rule
  // applies (a foreign address keeps the region name in the same field).
  _isItalyPayload(hereAddress, addressItem) {
    const code = (hereAddress?.countryCode ?? addressItem?.location_address_country_code ?? '').toLowerCase();
    const country = (hereAddress?.countryName ?? addressItem?.location_address_country ?? '').toLowerCase();
    return code === 'ita' || code === 'it' || country === 'italia' || country === 'italy';
  }
  get isCapDisabled() {
    return this.disabled || this._isItaly && this._capFromHere;
  }
  get item() {
    return this.args.item;
  }
  get _hereApiKey() {
    return getConfig(this).APP?.here?.apiKey ?? HERE_API_KEY_FALLBACK;
  }
  get addresses() {
    return this.args.addresses;
  }
  get applicationController() {
    return this.args.applicationController;
  }
  get changeAction() {
    return this.args.changeAction;
  }
  get paesiMondoWithCountry() {
    const paesi = [...paesiMondo];
    const country = this.item?.location_address_country;
    if (country && !paesi.includes(country)) {
      paesi.push(country);
    }
    return paesi;
  }

  // ── HERE autocomplete ────────────────────────────────────────────────────

  _streetDropdownEl() {
    return document.getElementById(`zf-street-here-${this.elementId}`);
  }
  _openStreetDropdown() {
    this._streetDropdownEl()?.classList.add('is-open');
    if (!this._dropdownOpen) {
      this._dropdownOpen = true;
      openDropdownCount += 1;
    }
    document.body?.classList.add('dropdown-open');
  }
  onStreetInput(event) {
    set(this.item, 'location_address_street', event.target.value);
    this.searchStreet.perform(event.target.value);
  }
  closeStreetDropdown() {
    this.streetSuggestions = [];
    this._streetDropdownEl()?.classList.remove('is-open');
    if (this._dropdownOpen) {
      this._dropdownOpen = false;
      openDropdownCount = Math.max(0, openDropdownCount - 1);
      if (openDropdownCount === 0) {
        document.body?.classList.remove('dropdown-open');
      }
    }
  }
  onComuneChange(comune) {
    if (!comune || !this.item) return;
    set(this.item, 'location_address_city', comune.comune);
    set(this.item, 'location_address_county', normalizeProvinceCode(comune.code_prov));
    set(this.item, 'location_address_postal_code', comune.cap ?? null);
    this._capFromHere = Boolean(comune.cap);
    this.recreateTextAddressTask.perform();
  }
  changeCountry(newC) {
    set(this.item, 'location_address_country', newC);
    const countryIndex = paesiMondo.indexOf(newC);
    set(this.item, 'location_address_country_code', countryIndex === -1 ? null : paesiMondoAlfa3[countryIndex]);
    set(this.item, 'location_address_city', null);
    set(this.item, 'location_address_county', null);
    set(this.item, 'location_address_postal_code', null);
    this._capFromHere = false;
    this.recreateTextAddressTask.perform();
  }
  setInvoiceAddress() {
    this.addresses.forEach(a => set(a, 'is_invoice', false));
    set(this.item, 'is_invoice', true);
  }
  setDeliveryAddress(value) {
    this.addresses.forEach(a => set(a, 'is_consegna', false));
    set(this.item, 'is_consegna', value);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_capFromHere", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "streetSuggestions", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "autoFillCountyTask", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "paesiMondoWithCountry", [cached], Object.getOwnPropertyDescriptor(_class.prototype, "paesiMondoWithCountry"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onStreetInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onStreetInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeStreetDropdown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "closeStreetDropdown"), _class.prototype), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "searchStreet", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "onStreetChange", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "searchComune", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "onComuneChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onComuneChange"), _class.prototype), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "recreateTextAddressTask", [_dec5], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "removeAddressTask", [_dec6], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "changeCountry", [action], Object.getOwnPropertyDescriptor(_class.prototype, "changeCountry"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setInvoiceAddress", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setInvoiceAddress"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setDeliveryAddress", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setDeliveryAddress"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, EditSingleAddressComponent);

export { EditSingleAddressComponent as default };
//# sourceMappingURL=edit-single-address.js.map
